import { useEffect, useState } from "react";
import { History as HistoryIcon, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { loadHistory, clearHistory } from "@/lib/history";
import { toast } from "sonner";

export default function HistoryPage() {
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    setEntries(loadHistory());
  }, []);

  const handleClear = () => {
    clearHistory();
    setEntries([]);
    toast.success("History cleared");
  };

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-50">
            Diagnosis History
          </h1>
          <p className="mt-3 text-slate-400 max-w-2xl">
            Your recent scans are saved locally on this device. Nothing is uploaded or tracked.
          </p>
        </div>
        {entries.length > 0 && (
          <Button
            onClick={handleClear}
            data-testid="clear-history-btn"
            variant="outline"
            className="rounded-full border-slate-700 bg-transparent text-slate-200 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500 gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Clear All
          </Button>
        )}
      </header>

      {entries.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-12 text-center" data-testid="history-empty">
          <HistoryIcon className="w-10 h-10 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No diagnoses yet. Run your first scan on the Diagnostic page.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="history-grid">
          {entries.map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-800 bg-[#161E2E] p-5 hover:border-amber-500/40 transition-colors" data-testid={`history-item-${e.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-display font-bold text-lg text-slate-50 leading-tight">
                  {e.issue_title}
                </h3>
                <span className="font-mono text-lg font-bold text-amber-500 flex-shrink-0">
                  {e.confidence}%
                </span>
              </div>
              {e.vehicle && (
                <div className="text-xs text-slate-400 font-mono mb-2">{e.vehicle}</div>
              )}
              <p className="text-sm text-slate-300 line-clamp-2 mb-3">{e.summary}</p>
              <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider">
                <span className={`px-2 py-0.5 rounded-full border ${
                  e.urgency === "DO_NOT_DRIVE" ? "bg-red-500/15 border-red-500 text-red-300" :
                  e.urgency === "IMMEDIATE" ? "bg-orange-500/15 border-orange-500 text-orange-300" :
                  e.urgency === "SERVICE_SOON" ? "bg-yellow-500/15 border-yellow-500 text-yellow-300" :
                  "bg-emerald-500/15 border-emerald-500 text-emerald-300"
                }`}>
                  {e.urgency.replace(/_/g, " ")}
                </span>
                <span className="text-slate-500">
                  {new Date(e.timestamp).toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
